import React, { useState, useEffect } from 'react';
import moment from 'moment';
import Box from '@mui/material/Box';
import { Modal, Button } from 'react-bootstrap';
import { handleError } from '../../shared/ErrorCatching';
import { Translate } from '../../shared/LanguageService';
import { getFavoriteImage } from './queries/getFavoriteImage';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { removeFavoriteImage } from './queries/removeFavoriteImage';
import { usePrevious } from '../../shared/hooks/previousValue';

type FavoriteImg = {
    id: number;
    reference: string;
    photographerName: string;
};

type FavoriteImageModalProps = {
    selectedImageId: number | null;
    showModal: boolean;
    onClose: () => void;
    images: FavoriteImg[];
};

function downloadImage(path: string) {
    window.location.href = path;
}

function FavoriteImageModal({
    showModal,
    onClose,
    images,
    selectedImageId
}: FavoriteImageModalProps) {
    let [currentImageId, setCurrentImageId] = useState<number | null>(selectedImageId);

    let {
        isError,
        error,
        data: selectedImage
    } = useQuery(['favorite-image', currentImageId], () => getFavoriteImage(currentImageId), {
        enabled: currentImageId !== null
    });

    let queryClient = useQueryClient();
    let removeFavoriteImageMutation = useMutation(
        ({ derivedOrderId, photoId }: { derivedOrderId: number; photoId: number }) =>
            removeFavoriteImage(derivedOrderId, photoId),
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(['favorite-images']);
            },
            onError: (error) => {
                handleError(error);
            }
        }
    );

    let previousImageId = usePrevious<number | null>(currentImageId);
    let previousSelectedImageId = usePrevious<number | null>(selectedImageId);
    useEffect(() => {
        if (
            (currentImageId === null || previousImageId !== currentImageId) &&
            selectedImageId !== null &&
            previousSelectedImageId !== selectedImageId
        ) {
            setCurrentImageId(selectedImageId);
        }
    }, [currentImageId, selectedImageId, images]);

    useEffect(() => {
        function handleKeyUp(event: KeyboardEvent) {
            let imageIdList: number[] = images.map((image) => {
                return image.id;
            });
            let currentIndex = imageIdList.indexOf(currentImageId);
            let imageId = 0;

            let isSteppingLeft = event.keyCode === 37;
            let isSteppingRight = event.keyCode === 39;

            if (isSteppingLeft) {
                if (currentIndex <= 0) {
                    return;
                } else if (currentIndex > 0) {
                    imageId = imageIdList[currentIndex - 1];
                }
            } else if (isSteppingRight) {
                if (currentIndex === imageIdList.length - 1) {
                    return;
                }

                imageId = imageIdList[currentIndex + 1];
            } else {
                return;
            }

            setCurrentImageId(imageId);
        }

        document.onkeyup = handleKeyUp;

        return () => {
            document.onkeyup = null;
        };
    }, [currentImageId, images]);

    if (isError) {
        handleError(error);
    }

    function handleClose() {
        onClose();
    }

    function handleRemoveFavoriteImage() {
        let { derivedOrderId, photoId } = selectedImage;
        removeFavoriteImageMutation.mutate({ derivedOrderId, photoId });
        setCurrentImageId(null);
        handleClose();
    }

    return (
        <Modal
            bsSize="lg"
            aria-labelledby="contained-modal-title-vcenter"
            show={showModal}
            onHide={handleClose}
            dialogClassName="content-aware-modal"
        >
            <Modal.Body className={'favorites-modal-body'}>
                <Box
                    sx={{
                        minWidth: '1000px',
                        minHeight: '670px',
                        display: 'flex',
                        justifyContent: 'center'
                    }}
                >
                    {selectedImage ? (
                        <img
                            loading="lazy"
                            alt="An image marked as favorite by our internal personal."
                            height={670}
                            className="favorites-modal-image"
                            src={`/api/file/${selectedImage.previewFile}`}
                        />
                    ) : null}
                </Box>
                <Box sx={{ borderLeft: '1px solid #333' }}>
                    <div className={'favorites-modal-info'}>
                        <div className={'favorites-modal-info-content'}>
                            <h4 className={'favorites-modal-header-wrapper'}>
                                {selectedImage ? selectedImage.imageName : '...'}
                            </h4>
                            {selectedImage ? (
                                <i className="favorite-image-header-hide">
                                    {selectedImage.imageName}
                                </i>
                            ) : null}
                            <Button
                                className={'favorites-modal-close-button'}
                                bsStyle="light"
                                onClick={handleClose}
                                bsSize="small"
                            >
                                <i
                                    className="material-icons"
                                    style={{
                                        marginLeft: '0.3125em'
                                    }}
                                >
                                    close
                                </i>
                            </Button>
                        </div>
                        <div>
                            <strong>{Translate('favorite-images/modal/ordernumber')}</strong>
                            <br />
                            {selectedImage ? (
                                <a
                                    className={'favorites-modal-order-number-link'}
                                    href={`/orders/${selectedImage.derivedOrderId}/image`}
                                >
                                    {selectedImage.orderCollectionNumber}:
                                    {selectedImage.orderNumber}
                                </a>
                            ) : null}
                            <br />
                            <br />
                            <strong>{Translate('favorite-images/modal/address')}</strong>
                            <br />
                            {selectedImage
                                ? `${selectedImage.address}, ${selectedImage.postalNumber}`
                                : null}
                            {selectedImage ? selectedImage.city : null}
                            <br />
                            <br />
                            <strong>{Translate('favorite-images/modal/photograph')}</strong>
                            <br />
                            {selectedImage ? selectedImage.photographerName : null}
                            <br />
                            <br />
                            <strong>{Translate('favorite-images/modal/broker')}</strong>
                            <br />
                            {selectedImage
                                ? `${selectedImage.brokerName}, ${selectedImage.brokerFirm}`
                                : null}
                            <br />
                            <br />
                            {selectedImage && selectedImage.favoritedBy !== null ? (
                                <div>
                                    <strong>{Translate('favorite-images/modal/markedby')}</strong>
                                    <br />
                                    {selectedImage.favoritedBy}
                                    <br />
                                    {selectedImage.favoritedTime &&
                                        moment(selectedImage.favoritedTime).format('L LT')}
                                    <br />
                                    <br />
                                </div>
                            ) : null}
                        </div>
                        <div className={'favorites-modal-action-buttons'}>
                            <a className={'favorites-modal-action-button-link'}>
                                <Button
                                    bsStyle="light"
                                    className={'favorites-modal-action-button'}
                                    style={{
                                        lineHeight: '44px'
                                    }}
                                    onClick={handleRemoveFavoriteImage}
                                >
                                    <i
                                        className="material-icons favorite-icon"
                                        style={{
                                            color: 'rgba(255, 0, 0)',
                                            marginRight: 0
                                        }}
                                    >
                                        favorite
                                    </i>
                                </Button>
                                <span
                                    className={'favorites-modal-action-button-text'}
                                    onClick={handleRemoveFavoriteImage}
                                >
                                    {Translate('favorite-images/modal/button/deselect')}
                                </span>
                            </a>
                            {selectedImage ? (
                                <a
                                    href={`/api/file/${selectedImage.originalFile}`}
                                    className={'favorites-modal-action-button-link'}
                                >
                                    <Button
                                        bsStyle="light"
                                        className={'favorites-modal-action-button'}
                                        style={{
                                            lineHeight: '44px'
                                        }}
                                        onClick={() =>
                                            downloadImage(`/api/file/${selectedImage.originalFile}`)
                                        }
                                    >
                                        <i
                                            className="material-icons"
                                            style={{ color: 'rgba(0, 0, 0, 0.54)', marginRight: 0 }}
                                        >
                                            file_download
                                        </i>
                                    </Button>
                                    <span
                                        className={'favorites-modal-action-button-text'}
                                        onClick={() =>
                                            downloadImage(`/api/file/${selectedImage.originalFile}`)
                                        }
                                    >
                                        {Translate('favorite-images/modal/button/download')}
                                    </span>
                                </a>
                            ) : null}
                        </div>
                    </div>
                </Box>
            </Modal.Body>
        </Modal>
    );
}

export default FavoriteImageModal;
